import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  Phone, 
  Mic, 
  Users, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Building2,
  Car,
  ShieldCheck,
  UtensilsCrossed,
  Wrench,
  Home,
  Scale,
  Smile,
  ShoppingCart,
  Menu,
  X,
  Stethoscope,
  PawPrint,
  Dumbbell,
  Shield,
  Lock,
  Globe,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const SPEAKR_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/v0cvlfy5_Modern%20AI%20voice%20intelligence%20logo.png";
const GSK_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/aqy9mc85_2LOGO%20GSK%20.png";

const API = `${process.env.REACT_APP_API_URL}/api`;

// Trusted by companies
const TRUSTED_COMPANIES = [
  { name: "Maple Dental Group", industry: "Healthcare" },
  { name: "Northern HVAC Services", industry: "HVAC" },
  { name: "Pacific Auto Care", industry: "Auto Repair" },
  { name: "Summit Insurance", industry: "Insurance" },
  { name: "Prairie Properties", industry: "Real Estate" },
  { name: "Bay Area Legal", industry: "Legal" }
];

// Key compliance badges for landing
const KEY_BADGES = [
  { name: "HIPAA Ready", icon: Shield, color: "emerald" },
  { name: "PIPEDA Compliant", icon: Lock, color: "red" },
  { name: "SOC 2 Type II", icon: Award, color: "purple" },
  { name: "GDPR Ready", icon: Globe, color: "blue" }
];

const INDUSTRY_ICONS = {
  hvac: Wrench,
  insurance: ShieldCheck,
  auto_repair: Car,
  grocery: ShoppingCart,
  dental: Smile,
  restaurant: UtensilsCrossed,
  real_estate: Home,
  legal: Scale,
  healthcare: Stethoscope,
  veterinary: PawPrint,
  plumbing: Wrench,
  electrical: Zap,
  property_management: Building2,
  fitness: Dumbbell,
  custom: Building2
};

export default function Landing() {
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "Features", href: "#features" },
    { name: "Industries", href: "#industries" },
    { name: "Pricing", href: "/pricing" },
    { name: "Resources", href: "/resources" },
    { name: "Contact", href: "/contact" }
  ];

  const fetchIndustries = async () => {
    try {
      // Log the actual URL being called for debugging
      console.log('[Landing] Fetching industries from:', `${API}/industries`);
      
      const res = await axios.get(`${API}/industries`);
      // Ensure we always set an array
      setIndustries(Array.isArray(res.data) ? res.data : []);
      setApiError(null);
    } catch (error) {
      console.error("[Landing] Failed to fetch industries:", error?.message);
      // Ensure industries stays as an array even on error
      setIndustries([]);
      // Surface network errors
      if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
        setApiError('API unreachable - check REACT_APP_API_URL');
      }
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  const features = [
    {
      icon: Phone,
      title: "Real Phone Calls",
      description: "Connect your existing cell phone. No new numbers needed."
    },
    {
      icon: Mic,
      title: "Human-Like Voice",
      description: "Ultra-realistic AI voices in English, Hindi & Punjabi"
    },
    {
      icon: Users,
      title: "Multiple Agents",
      description: "Receptionist, sales, support - all working 24/7"
    },
    {
      icon: Zap,
      title: "Instant Setup",
      description: "Pre-built industry templates. Live in minutes."
    }
  ];

  const benefits = [
    "Never miss a customer call again",
    "24/7 availability without hiring staff",
    "Multilingual support (English, Hindi, Punjabi)",
    "Book appointments automatically",
    "Capture leads while you sleep",
    "Professional image for your business"
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glassmorphism border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="hover:opacity-90 transition-opacity">
              <img src={SPEAKR_LOGO} alt="Speakr" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => {
                    if (link.href.startsWith('/')) {
                      e.preventDefault();
                      navigate(link.href);
                    }
                  }}
                  className="text-sm font-medium text-muted-foreground hover:text-white transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                Login
              </Button>
              <Button 
                className="rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"
                onClick={() => navigate('/register')}
                data-testid="get-started-btn"
              >
                Get Started Free
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-muted-foreground hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => {
                      if (link.href.startsWith('/')) {
                        e.preventDefault();
                        navigate(link.href);
                      }
                      setMobileMenuOpen(false);
                    }}
                    className="text-muted-foreground hover:text-white py-2"
                  >
                    {link.name}
                  </a>
                ))}
                <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                  <Button variant="ghost" className="justify-start" onClick={() => navigate('/login')}>
                    Login
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"
                    onClick={() => navigate('/register')}
                  >
                    Get Started Free
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative">
          <img 
            src={SPEAKR_LOGO_BLACK} 
            alt="Speakr" 
            className="w-64 h-auto mx-auto mb-8"
          />
          <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight mb-6">
            AI Agents That
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent"> Answer Your Phone</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Give your small business a professional receptionist, sales agent, and support team — 
            all powered by AI, using your existing phone number.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="rounded-full px-8 py-6 text-lg bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 shadow-lg shadow-blue-500/25"
              onClick={() => navigate('/register')}
              data-testid="hero-cta-btn"
            >
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}
            >
              Watch Demo
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-card/30" id="features">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Everything You Need to Never Miss a Call
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 px-6" id="industries">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">
            Built for Your Industry
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Pre-trained AI agents that understand your business. Just pick your industry and go live.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.isArray(industries) && industries.filter(i => i.id !== 'custom').map((industry) => {
              const Icon = INDUSTRY_ICONS[industry.id] || Building2;
              return (
                <Card 
                  key={industry.id}
                  className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group"
                  onClick={() => navigate(`/register?industry=${industry.id}`)}
                  data-testid={`industry-${industry.id}`}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-7 h-7 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h3 className="font-medium text-sm">{industry.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {industry.agent_count} agent{industry.agent_count !== 1 ? 's' : ''} included
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-primary/5 via-purple-500/5 to-primary/5 border-y border-border/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <Award className="w-3 h-3 mr-1" />
              Trusted by 500+ Businesses
            </Badge>
            <h2 className="text-2xl font-heading font-bold">
              Businesses Across North America Choose Speakr
            </h2>
          </div>

          {/* Company Logos */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {TRUSTED_COMPANIES.map((company, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-4 py-2 bg-card/50 rounded-lg border border-border/50"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">
                    {company.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium">{company.name}</p>
                  <p className="text-xs text-muted-foreground">{company.industry}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Compliance Badges */}
          <div className="flex flex-wrap justify-center gap-4">
            {KEY_BADGES.map((badge, idx) => {
              const Icon = badge.icon;
              const colorMap = {
                emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
                red: "text-red-400 bg-red-500/10 border-red-500/30",
                purple: "text-purple-400 bg-purple-500/10 border-purple-500/30",
                blue: "text-blue-400 bg-blue-500/10 border-blue-500/30"
              };
              return (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${colorMap[badge.color]}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium text-white">{badge.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 bg-gradient-to-b from-card/50 to-background">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">
            Why Small Businesses Love Speakr
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-card/50">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Smart AI Agents for Modern Businesses
          </h2>
          <p className="text-muted-foreground mb-8">
            Never miss a call. Never miss a lead. AI-powered reception, sales, and industry agents.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-2">AI Receptionist</h3>
                <div className="text-3xl font-bold mb-4">$399<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>24/7 AI call answering</li>
                  <li>1,000 minutes/month</li>
                  <li>Message taking & routing</li>
                  <li>Multilingual support</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-2">AI Sales Agent</h3>
                <div className="text-3xl font-bold mb-4">$699<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>Lead qualification</li>
                  <li>1,500 minutes/month</li>
                  <li>Appointment booking</li>
                  <li>CRM integration</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-b from-primary/20 to-card border-primary/50 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full">
                Most Popular
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-2">SMB Power Pack</h3>
                <div className="text-3xl font-bold mb-4">$999<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>Receptionist + Sales</li>
                  <li>2,500 shared minutes</li>
                  <li>All features included</li>
                  <li>Priority support</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-2">AI Insurance</h3>
                <div className="text-3xl font-bold mb-4">$999<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>Compliance-aware</li>
                  <li>2,500 minutes/month</li>
                  <li>FSRA/RIBO ready</li>
                  <li>Multi-department</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8">
            <Button size="lg" onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-blue-600 to-purple-600">
              View All Plans & Pricing
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-0">
            <CardContent className="p-0">
              <div className="relative bg-gradient-to-r from-[#0a0a1a] via-[#0d1033] to-[#0a0a1a] p-12 text-center">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
                </div>
                <div className="relative">
                  <img src={SPEAKR_LOGO_BLACK} alt="Speakr" className="w-48 h-auto mx-auto mb-6" />
                  <h2 className="text-3xl font-heading font-bold mb-4">
                    Ready to Transform Your Business?
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Join hundreds of small businesses using AI to never miss a call again.
                  </p>
                  <Button 
                    size="lg" 
                    className="rounded-full px-10 py-6 text-lg bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"
                    onClick={() => navigate('/register')}
                    data-testid="final-cta-btn"
                  >
                    Start Your Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-4 mb-4">
                <img src={GSK_LOGO} alt="GSK Productions" className="w-10 h-10 rounded-full" />
                <div>
                  <p className="text-xs text-muted-foreground">Developed by</p>
                  <p className="font-medium">GSK Productions Inc.</p>
                </div>
              </Link>
              <p className="text-sm text-muted-foreground">
                AI-powered voice agents for modern businesses across North America.
              </p>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#industries" className="hover:text-white transition-colors">Industries</a></li>
                <li><Link to="/pricing#bundles" className="hover:text-white transition-colors">Bundles</Link></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/security" className="hover:text-white transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Speakr™ by GSK Productions Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span>🇨🇦 Toronto, Canada</span>
              <span>•</span>
              <span>Canada & USA Support</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
